import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReferralProgram = ({ referralData, onShareReferral }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyReferralCode = () => {
    navigator.clipboard?.writeText(referralData?.referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const shareText = `Join CryptoPredict Pro and get advanced AI trading predictions! Use my referral code: ${referralData?.referralCode}`;
    const shareUrl = `https://cryptopredict.pro/register?ref=${referralData?.referralCode}`;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`);
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
        break;
      case 'email':
        window.open(`mailto:?subject=Join CryptoPredict Pro&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`);
        break;
      default:
        break;
    }
    onShareReferral(platform);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-card-foreground">Referral Program</h2>
        <p className="text-sm text-muted-foreground">Earn rewards by referring friends to CryptoPredict Pro</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Referral Stats */}
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={16} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                  <p className="text-xl font-semibold text-foreground">{referralData?.totalReferrals}</p>
                </div>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="DollarSign" size={16} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                  <p className="text-xl font-semibold text-foreground">{formatCurrency(referralData?.totalEarned)}</p>
                </div>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-xl font-semibold text-foreground">{formatCurrency(referralData?.pendingEarnings)}</p>
                </div>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-xl font-semibold text-foreground">{referralData?.monthlyReferrals}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Referrals */}
          <div>
            <h3 className="font-medium text-card-foreground mb-3">Recent Referrals</h3>
            <div className="space-y-3">
              {referralData?.recentReferrals?.map((referral) => (
                <div key={referral?.id} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{referral?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Joined {new Date(referral.joinDate)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-success">{formatCurrency(referral?.reward)}</p>
                    <p className={`text-xs ${referral?.status === 'paid' ? 'text-success' : 'text-warning'}`}>
                      {referral?.status === 'paid' ? 'Paid' : 'Pending'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Referral Actions */}
        <div className="space-y-6">
          {/* Referral Code */}
          <div className="bg-background border border-border rounded-lg p-4">
            <h3 className="font-medium text-card-foreground mb-3">Your Referral Code</h3>
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-foreground">
                {referralData?.referralCode}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyReferralCode}
                iconName={copied ? "Check" : "Copy"}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this code with friends to earn {formatCurrency(referralData?.rewardPerReferral)} for each successful referral
            </p>
          </div>

          {/* Share Options */}
          <div className="bg-background border border-border rounded-lg p-4">
            <h3 className="font-medium text-card-foreground mb-3">Share Your Link</h3>
            <div className="grid grid-cols-1 gap-3">
              <Button
                variant="outline"
                onClick={() => handleShare('twitter')}
                iconName="Twitter"
                iconPosition="left"
                fullWidth
              >
                Share on Twitter
              </Button>
              <Button
                variant="outline"
                onClick={() => handleShare('linkedin')}
                iconName="Linkedin"
                iconPosition="left"
                fullWidth
              >
                Share on LinkedIn
              </Button>
              <Button
                variant="outline"
                onClick={() => handleShare('email')}
                iconName="Mail"
                iconPosition="left"
                fullWidth
              >
                Share via Email
              </Button>
            </div>
          </div>

          {/* Program Details */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-medium text-card-foreground mb-3">How It Works</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary-foreground">1</span>
                </div>
                <p className="text-sm text-card-foreground">Share your referral code with friends</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary-foreground">2</span>
                </div>
                <p className="text-sm text-card-foreground">They sign up and subscribe to any paid plan</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary-foreground">3</span>
                </div>
                <p className="text-sm text-card-foreground">You earn {formatCurrency(referralData?.rewardPerReferral)} credited to your account</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;